import React, { lazy, Suspense, useRef, useState, useTransition } from 'react';

import ErrorBoundaryWithCrashReporting from '../../../../../components/Error/ErrorBoundaryWithCrashReporting';
import useShowCreditProductPurchaseSuccessModal from '../../CreditProductPurchaseSuccessModal/useShowCreditProductPurchaseSuccessModal';
import { CreditProductPurchaseOrderForAfterPayment } from '../../types';
import {
  ContactPacksPurchaseModalContext,
  OpenModalOptions,
} from './ContactPacksPurchaseModalContext';

type Props = {
  children: React.ReactNode;
};

const ContactPacksPurchaseModal = lazy(
  () => import('../ContactPacksPurchaseModal')
);
const ContactPacksPurchaseModalForAfterPaymentHandlers = lazy(
  () => import('../ContactPacksAfterPaymentHandlers')
);
export default function ContactPacksPurchaseModalProvider({ children }: Props) {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [errorBannerProviderInitialValue, setErrorBannerProviderInitialValue] =
    useState<OpenModalOptions['errorBannerProviderInitialValue']>(undefined);
  const onCloseCallbackRef = useRef<OpenModalOptions['onClose']>(undefined);
  const onCloseCallback = onCloseCallbackRef.current;

  const [isModalChunkLoadPending, startTransition] = useTransition();

  const {
    creditProductPurchaseSuccessModal,
    openCreditProductPurchaseSuccessModal,
  } = useShowCreditProductPurchaseSuccessModal();

  const openContactPacksPurchaseModal = ({
    callback,
    errorBannerProviderInitialValue,
    onClose,
  }: OpenModalOptions = {}) => {
    if (isModalChunkLoadPending) {
      return;
    }
    setErrorBannerProviderInitialValue(errorBannerProviderInitialValue);
    onCloseCallbackRef.current = onClose;

    startTransition(() => {
      setIsModalOpen(true);
      return callback?.();
    });
  };

  const handleCloseModal = (
    creditProductPurchaseOrder?: CreditProductPurchaseOrderForAfterPayment
  ) => {
    onCloseCallback?.(creditProductPurchaseOrder);
    if (creditProductPurchaseOrder) {
      openCreditProductPurchaseSuccessModal(creditProductPurchaseOrder);
    }
    onCloseCallbackRef.current = undefined;
    setErrorBannerProviderInitialValue(undefined);
    setIsModalOpen(false);
  };

  return (
    <ContactPacksPurchaseModalContext.Provider
      value={{
        openContactPacksPurchaseModal,
        isModalChunkLoadPending,
      }}
    >
      <ErrorBoundaryWithCrashReporting fallback={null}>
        <Suspense fallback={null}>
          <ContactPacksPurchaseModalForAfterPaymentHandlers />
          {creditProductPurchaseSuccessModal}
          {isModalOpen && (
            <ContactPacksPurchaseModal
              onClose={handleCloseModal}
              errorBannerProviderInitialValue={errorBannerProviderInitialValue}
            />
          )}
        </Suspense>
      </ErrorBoundaryWithCrashReporting>
      {children}
    </ContactPacksPurchaseModalContext.Provider>
  );
}
